/**
 * Client-side script → scenes parser.
 *
 * Feb 13 2026 — added because the storyboard LLM was collapsing multi-scene
 * scripts into a single storyboard scene despite explicit rules. Now we
 * parse the script client-side to know how many scenes there SHOULD be,
 * pass that count down to the backend as a hint, and warn loudly when
 * the returned storyboard has fewer scenes than the script demands.
 *
 * Scene detection is deliberately conservative — we only split on
 * markers that unambiguously mean "new scene":
 *   - "SCENE 1", "SCENE 2", "Scene: <title>"
 *   - "INT. <location> - <time>", "EXT. <location> - <time>"
 *   - Numbered scene lists at start-of-line: "1. …" "2. …"
 *
 * We do NOT split on paragraph breaks alone — that's too aggressive and
 * would produce false positives on well-formatted prose scripts.
 */

/**
 * Parse an input script into a list of scenes.
 * @param {string} script
 * @returns {Array<{ index: number, header: string, body: string, title: string }>}
 */
export const parseScriptScenes = (script) => {
    if (!script || typeof script !== 'string') return [];
    const trimmed = script.trim();
    if (!trimmed) return [];

    // Scene header patterns, ordered by specificity (most specific first).
    // Each returns a match with the full header string, capturing at group
    // 1 the number (if any) and group 2 the title text.
    const HEADER_PATTERNS = [
        // "SCENE 1", "Scene 2:", "SCENE 3 - Kitchen"
        /^[\t ]*Scene[\s]+(\d+)[.\s:—\-]*(.*)$/gim,
        // "INT. KITCHEN - MORNING", "EXT. STREET — NIGHT"
        /^[\t ]*(INT\.|EXT\.)[\s]*([^\n]+)$/gim,
        // "1. Kitchen — morning", "2. Street" (only when clearly a scene list)
        /^[\t ]*(\d+)[.)][\s]+([A-Z][^\n]{5,})$/gim,
    ];

    // Collect every match position across all patterns.
    const markers = [];
    for (const rx of HEADER_PATTERNS) {
        let m;
        rx.lastIndex = 0;
        while ((m = rx.exec(trimmed)) !== null) {
            markers.push({
                start: m.index,
                header: m[0].trim(),
                number: m[1] ? Number(m[1]) : null,
                title: (m[2] || '').trim(),
            });
        }
    }
    // Sort by position, dedupe overlaps (nearby markers within 3 chars).
    markers.sort((a, b) => a.start - b.start);
    const deduped = [];
    for (const mk of markers) {
        if (deduped.length && mk.start - deduped[deduped.length - 1].start < 3) continue;
        deduped.push(mk);
    }

    if (deduped.length === 0) {
        // No explicit scene headers found — the whole script is one scene.
        return [{ index: 1, header: '', body: trimmed, title: '' }];
    }

    // Split the script at each marker's start position.
    const scenes = [];
    for (let i = 0; i < deduped.length; i++) {
        const start = deduped[i].start;
        const end = i + 1 < deduped.length ? deduped[i + 1].start : trimmed.length;
        const chunk = trimmed.slice(start, end).trim();
        const header = deduped[i].header;
        // Body = everything after the header line.
        const body = chunk.slice(header.length).replace(/^[.\s:—\-]+/, '').trim();
        scenes.push({
            index: i + 1,
            header,
            body,
            title: deduped[i].title || header,
        });
    }
    return scenes;
};

/**
 * Convenience: return just the number of scenes the script defines.
 */
export const countScriptScenes = (script) => {
    return parseScriptScenes(script).length;
};

/**
 * Convenience: extract just the scene titles for passing to the backend
 * as generation hints.
 */
export const scriptSceneTitles = (script) => {
    return parseScriptScenes(script).map(s => s.title || s.header || `Scene ${s.index}`);
};
